import moment from "moment";
import withErrorCheck from "../CheckError";
import server from "../Server";

export type GetPlayerMonthlyFormPayload = { pid: number };

export type PlayerMonthlyFormRow = {
  month_key: string; // e.g. "2025-11"
  month_label: string; // e.g. "Nov 2025"
  matches: number;
  avg_rating: number;
  goals: number;
  assists: number;
  wins: number;
};

export const GetPlayerMonthlyForm = async (
  { pid }: GetPlayerMonthlyFormPayload
): Promise<PlayerMonthlyFormRow[]> => {
  const now = moment();
  const startOfCurrentMonth = now.clone().startOf("month");

  // We want the last 3 *full* months before the current one
  const month1Start = startOfCurrentMonth.clone().subtract(1, "month");
  const month2Start = startOfCurrentMonth.clone().subtract(2, "month");
  const month3Start = startOfCurrentMonth.clone().subtract(3, "month");

  const from = month3Start.toISOString();
  const to = startOfCurrentMonth.toISOString();

  const response = await server
    .from("player_games_view")
    .select([
      "game_datetime",
      "game_status",
      "player_id",
      "player_attended",
      "player_rating",
      "player_goals_scored",
      "player_assists_made",
      "player_outcome",
    ].join(","))
    .eq("player_id", pid)
    .eq("player_attended", true)
    .eq("game_status", "FINALIZED")
    .gte("game_datetime", from)
    .lt("game_datetime", to);

  const rows = withErrorCheck<any[]>(response as any) || [];

  // Helper to init a month bucket
  const createMonthBucket = (m: moment.Moment): PlayerMonthlyFormRow => ({
    month_key: m.format("YYYY-MM"),
    month_label: m.format("MMM YYYY"),
    matches: 0,
    avg_rating: 0,
    goals: 0,
    assists: 0,
    wins: 0,
  });

  const buckets: Record<string, PlayerMonthlyFormRow> = {};

  // Ensure we always have 3 buckets, even if there were no games
  [month3Start, month2Start, month1Start].forEach((m) => {
    const key = m.format("YYYY-MM");
    buckets[key] = createMonthBucket(m);
  });

  // Aggregate rows into the corresponding month bucket
  rows.forEach((row) => {
    const m = moment(row.game_datetime);
    const key = m.format("YYYY-MM");

    const bucket = buckets[key];
    if (!bucket) {
      return;
    }

    bucket.matches += 1;

    if (typeof row.player_rating === "number" && !isNaN(row.player_rating)) {
      // For simplicity, keep a running average
      bucket.avg_rating =
        bucket.avg_rating === 0
          ? row.player_rating
          : (bucket.avg_rating * (bucket.matches - 1) + row.player_rating) / bucket.matches;
    }

    bucket.goals += Number(row.player_goals_scored ?? 0);
    bucket.assists += Number(row.player_assists_made ?? 0);
    if (row.player_outcome === "W") {
      bucket.wins += 1;
    }
  });

  // Round avg_rating to one decimal place
  const result = [month3Start, month2Start, month1Start].map((m) => {
    const key = m.format("YYYY-MM");
    const bucket = buckets[key] || createMonthBucket(m);
    return {
      ...bucket,
      avg_rating:
        bucket.avg_rating > 0 ? Math.round(bucket.avg_rating * 10) / 10 : 0,
    };
  });

  return result;
};
